import {
  useCallback,
} from 'react';

import {
  fetchWithRetry,
} from '@/lib/apiRequest';

export type PublicationLinkClient = {
  id: string;
  name: string;
};

type ApiError = {
  success?: boolean;
  error?: string;
};

export function usePublicationInboxLinking() {
  const loadClients =
    useCallback(
      async (): Promise<
        PublicationLinkClient[]
      > => {
        const response =
          await fetchWithRetry(
            '/api/clients',
            {},
            {
              timeoutMs: 15000,
              retries: 1,
              retryDelayMs: 300,
            }
          );

        const data =
          (await response.json()) as
            ApiError & {
              clients?: Array<{
                id?: string;
                name?: string;
              }>;
            };

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
              'Não foi possível carregar os clientes.'
          );
        }

        return (
          data.clients ?? []
        )
          .map(
            (client) => ({
              id:
                String(
                  client.id ?? ''
                ).trim(),
              name:
                String(
                  client.name ?? ''
                ).trim(),
            })
          )
          .filter(
            (client) =>
              client.id &&
              client.name
          )
          .sort(
            (left,right) =>
              left.name.localeCompare(
                right.name,
                'pt-BR'
              )
          );
      },
      []
    );

  const createCaseFromPublication =
    useCallback(
      async (
        input: {
          clientId: string;
          processNumber: string;
          tribunal?: string | null;
        }
      ) => {
        const clientId =
          input.clientId.trim();
        const processNumber =
          input.processNumber.trim();

        if (!clientId) {
          throw new Error(
            'Selecione o cliente.'
          );
        }

        if (!processNumber) {
          throw new Error(
            'A publicação não possui número de processo.'
          );
        }

        const response =
          await fetchWithRetry(
            '/api/cases',
            {
              method: 'POST',
              headers: {
                'Content-Type':
                  'application/json',
              },
              body:
                JSON.stringify({
                  clientId,
                  title:
                    `Processo ${processNumber}`,
                  caseNumber:
                    processNumber,
                  court:
                    input.tribunal ??
                    null,
                  status:
                    'active',
                  priority:
                    'normal',
                  notes:
                    'Caso criado a partir da Inbox Jurídica.',
                }),
            },
            {
              timeoutMs: 20000,
              retries: 0,
            }
          );

        const data =
          (await response.json()) as
            ApiError & {
              case?: {
                id?: string;
                caseNumber?: string | null;
              } | null;
              publicationReconciliation?: {
                scanned?: number;
                matched?: number;
                unmatched?: number;
                ambiguous?: number;
                invalid?: number;
              } | null;
            };

        if (
          !response.ok ||
          !data.success
        ) {
          throw new Error(
            data.error ||
              'Não foi possível criar o caso.'
          );
        }

        const caseId =
          String(
            data.case?.id ?? ''
          ).trim();

        if (!caseId) {
          throw new Error(
            'O caso foi processado, mas o identificador não retornou.'
          );
        }

        return {
          caseId,
          reconciliation:
            data.publicationReconciliation ??
            null,
        };
      },
      []
    );

  return {
    loadClients,
    createCaseFromPublication,
  };
}